import { useAlertEmit } from "@fpsoftware/mvp";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SessionConsumeBody, SessionRequestBody } from "shared";
import { Api } from "@/api.config";
import { getErrorMessage } from "@/infrastructure/errors";
import { QueryKeys } from "@/LinkGen";
import type { RegistrationFormData } from "@/pages/PageSignUp";

export const useAuth = () => {
  const emit = useAlertEmit();
  const queryClient = useQueryClient();

  const requestSession = useMutation({
    mutationFn: async (x: SessionRequestBody) => {
      await Api.RequestSession(x);
    },
    onSuccess: () =>
      emit({
        type: "success",
        content:
          "You have been sent a login email. Check your email to proceed.",
      }),
    onError: (e) => emit({ type: "error", content: getErrorMessage(e) }),
  });

  const consumeSession = useMutation({
    mutationFn: async (x: SessionConsumeBody) => {
      await Api.ConsumeSessionRequest(x);
      await queryClient.refetchQueries({
        queryKey: QueryKeys.Profile(),
      });
    },
    onSuccess: () =>
      emit({
        type: "success",
        content: "You have successfully logged in.",
      }),
  });

  const logout = useMutation({
    mutationFn: async () => {
      await Api.Logout();
      await queryClient.refetchQueries({
        queryKey: QueryKeys.Profile(),
      });
    },
    onSuccess: () =>
      emit({
        type: "success",
        content: "You have successfully logged out.",
      }),
  });

  const signUp = useMutation({
    mutationFn: async (data: RegistrationFormData) => {
      const formData = new FormData();
      formData.append("companyName", data.companyName);
      formData.append("companyNmls", data.companyNmls);
      formData.append("companyAddress", data.companyAddress);
      formData.append("mpName", data.mpName);
      formData.append("mpEmail", data.mpEmail);
      formData.append("mpPhone", data.mpPhone);
      formData.append("mpNmls", data.mpNmls);
      formData.append("mpLicenseNumber", data.mpLicenseNumber);
      formData.append("logo", data.logo);
      await Api.Register(formData);
    },
    onSuccess: () => {
      emit({
        type: "success",
        content: "Registration successful! You can now log in.",
      });
    },
    onError: (e) => {
      emit({ type: "error", content: getErrorMessage(e) });
    },
  });

  return {
    requestSession,
    consumeSession,
    logout,
    signUp,
  };
};
